import { searchTypeToEntityType } from './prospectingEntityType';
import { canonicalizeIndustryValues } from './industryCanonicalization';

const COUNTRY_CODE_TO_NAME = {
  DK: 'Denmark',
  SE: 'Sweden',
  NO: 'Norway',
  DE: 'Germany',
  ES: 'Spain',
  GB: 'United Kingdom',
  US: 'United States',
  NL: 'Netherlands',
};

const COUNTRY_NAME_FORMATTER = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

const EMPLOYEE_RANGE_BOUNDS = {
  '1-10': { gte: 1, lte: 10 },
  '11-50': { gte: 11, lte: 50 },
  '51-200': { gte: 51, lte: 200 },
  '201-1000': { gte: 201, lte: 1000 },
  '1000+': { gte: 1000 },
};

function normalizeString(value) {
  return String(value || '').trim();
}

function isIndustryFieldKey(fieldKey) {
  return fieldKey === 'industries' || fieldKey === 'company.industries';
}

function dedupe(values = []) {
  return Array.from(new Set(values.filter((value) => normalizeString(value).length > 0)));
}

function countryCodeToLabel(code) {
  const normalized = normalizeString(code).toUpperCase();
  if (!normalized) return '';

  const mappedLabel = COUNTRY_CODE_TO_NAME[normalized];
  if (mappedLabel) return mappedLabel;

  const intlLabel = COUNTRY_NAME_FORMATTER?.of(normalized);
  if (typeof intlLabel === 'string' && intlLabel.trim()) return intlLabel;

  return normalized;
}

function normalizeNumberRange(range = {}) {
  const gte = range?.gte !== undefined && range?.gte !== null ? Number(range.gte) : null;
  const lte = range?.lte !== undefined && range?.lte !== null ? Number(range.lte) : null;
  const normalized = {};

  if (Number.isFinite(gte)) normalized.gte = gte;
  if (Number.isFinite(lte)) normalized.lte = lte;

  return Object.keys(normalized).length > 0 ? normalized : null;
}

function formatYearRange(range = {}) {
  if (range.gte !== undefined && range.lte !== undefined) return `${range.gte}-${range.lte}`;
  if (range.gte !== undefined) return `${range.gte}+`;
  return `through ${range.lte}`;
}

function employeeRangeFromBounds(range = {}) {
  const gte = Number.isFinite(range?.gte) ? Number(range.gte) : null;
  const lte = Number.isFinite(range?.lte) ? Number(range.lte) : null;

  if (gte === null && lte === null) return [];

  const exact = Object.entries(EMPLOYEE_RANGE_BOUNDS).find(([, bounds]) => {
    const boundsGte = bounds.gte ?? null;
    const boundsLte = bounds.lte ?? null;
    return gte === boundsGte && lte === boundsLte;
  });
  if (exact) return [exact[0]];

  const overlap = Object.entries(EMPLOYEE_RANGE_BOUNDS)
    .filter(([, bounds]) => {
      const boundsGte = bounds.gte ?? Number.NEGATIVE_INFINITY;
      const boundsLte = bounds.lte ?? Number.POSITIVE_INFINITY;
      const filterGte = gte ?? Number.NEGATIVE_INFINITY;
      const filterLte = lte ?? Number.POSITIVE_INFINITY;
      return boundsLte >= filterGte && boundsGte <= filterLte;
    })
    .map(([label]) => label);

  return overlap;
}

function makeFilter(field, op, value, label) {
  return {
    field,
    op,
    value,
    label,
    source: 'ai',
  };
}

export function mapUiFiltersToCanonical(searchType, filters = []) {
  const entityType = searchTypeToEntityType(searchType);
  const canonical = {};

  for (const filter of filters || []) {
    applyUiFilter(canonical, filter, entityType);
  }

  return canonical;
}

export function mapCanonicalFiltersToUi(searchType, canonical = {}) {
  const entityType = searchTypeToEntityType(searchType);
  const filters = [];

  const countryCodes = dedupe(canonical.country_codes || []);
  if (countryCodes.length > 0) {
    const countries = countryCodes.map(countryCodeToLabel);
    filters.push(makeFilter('country', 'in', countries, `Geography: ${countries.join(', ')}`));
  }

  const cities = dedupe(canonical.cities || []);
  if (cities.length > 0) {
    filters.push(makeFilter('city', 'in', cities, `City: ${cities.join(', ')}`));
  }

  const industries = dedupe(
    canonicalizeIndustryValues([
      ...(canonical.industries || []),
      ...(canonical.company?.industries || []),
    ])
  );
  if (industries.length > 0) {
    filters.push(makeFilter('industry', 'in', industries, `Industry: ${industries.join(', ')}`));
  }

  const employeeRanges = dedupe(canonical.employee_count_ranges || []);
  if (employeeRanges.length > 0) {
    const field = entityType === 'company' ? 'employeeRange' : 'companySize';
    filters.push(makeFilter(field, 'in', employeeRanges, `Size: ${employeeRanges.join(' / ')}`));
  }

  const foundedYearRange = entityType === 'company'
    ? normalizeNumberRange(canonical.founded_year)
    : null;
  if (foundedYearRange) {
    filters.push(makeFilter(
      'foundedYear',
      'between',
      foundedYearRange,
      `Founded: ${formatYearRange(foundedYearRange)}`
    ));
  }

  const companyRange = canonical.company?.employee_count;
  const companyRangeLabels = employeeRangeFromBounds(companyRange);
  if (companyRangeLabels.length > 0) {
    const field = entityType === 'company' ? 'employeeRange' : 'companySize';
    filters.push(makeFilter(field, 'in', companyRangeLabels, `Size: ${companyRangeLabels.join(' / ')}`));
  }

  const seniorities = dedupe(canonical.seniorities || []);
  if (seniorities.length > 0) {
    filters.push(
      seniorities.length === 1
        ? makeFilter('seniority', 'equals', seniorities[0], `Seniority: ${seniorities[0]}`)
        : makeFilter('seniority', 'in', seniorities, `Seniority: ${seniorities.join(', ')}`)
    );
  }

  const functions = dedupe(canonical.functions || []);
  if (functions.length > 0) {
    filters.push(
      functions.length === 1
        ? makeFilter('role', 'contains', functions[0], `Role: ${functions[0]}`)
        : makeFilter('role', 'in', functions, `Role: ${functions.join(', ')}`)
    );
  }

  const highlights = dedupe(canonical.highlights_any || []);
  if (highlights.length > 0) {
    filters.push(makeFilter('insights', 'hasInsight', highlights, `Signal: ${highlights.join(', ')}`));
  }

  const companyTags = dedupe(canonical.company_tags_any || []);
  if (companyTags.length > 0) {
    const lowered = companyTags.map((value) => value.toLowerCase());
    const nonFunding = companyTags.filter((value) => value.toLowerCase() !== 'recently funded');

    if (lowered.includes('recently funded')) {
      filters.push(makeFilter('recentFunding', 'equals', true, 'Recently funded'));
    }
    if (nonFunding.length > 0) {
      filters.push(makeFilter('insights', 'hasInsight', nonFunding, `Signal: ${nonFunding.join(', ')}`));
    }
  }

  return dedupeFilters(filters);
}

function applyUiFilter(filters, filter, entityType) {
  const field = normalizeString(filter?.field);
  const values = toValueList(filter?.value);

  if (field === 'country') {
    addListValues(filters, 'country_codes', values.map(countryToCode).filter(Boolean));
    return;
  }

  if (field === 'city') {
    addListValues(filters, 'cities', values);
    return;
  }

  if (field === 'industry') {
    addListValues(filters, 'industries', canonicalizeIndustryValues(values));
    return;
  }

  if (field === 'employeeRange') {
    addListValues(filters, 'employee_count_ranges', values);
    return;
  }

  if (field === 'foundedYear' && entityType === 'company') {
    const foundedYearRange = rangeFromFilter(filter);
    if (foundedYearRange) {
      filters.founded_year = unionRanges(filters.founded_year, foundedYearRange);
    }
    return;
  }

  if (field === 'companySize') {
    const companyFilter = ensureCompanyFilter(filters);
    companyFilter.employee_count = unionRanges(companyFilter.employee_count, rangesToNumberRange(values));
    return;
  }

  if (field === 'seniority' && entityType === 'person') {
    addListValues(filters, 'seniorities', values.map(normalizeSeniority).filter(Boolean));
    return;
  }

  if (field === 'role' && entityType === 'person') {
    const functions = values.map(normalizeFunction).filter(Boolean);
    if (functions.length > 0) {
      addListValues(filters, 'functions', functions);
      return;
    }
    addListValues(filters, 'titles_any', values);
    return;
  }

  if (field === 'insights') {
    if (entityType === 'company') {
      addListValues(filters, 'company_tags_any', values);
      return;
    }
    addListValues(filters, 'highlights_any', values);
    return;
  }

  if (field === 'recentFunding' && entityType === 'company' && Boolean(filter?.value)) {
    addListValues(filters, 'company_tags_any', ['recently funded']);
  }
}

function dedupeFilters(filters = []) {
  const seen = new Set();
  const deduped = [];

  for (const filter of filters) {
    const signature = `${filter.field}:${filter.op}:${JSON.stringify(filter.value)}`;
    if (seen.has(signature)) continue;
    seen.add(signature);
    deduped.push(filter);
  }

  return deduped;
}

function operationToToolCall(operation = {}) {
  const op = normalizeString(operation.op || operation.type || operation.name);
  const field = normalizeString(operation.field_key || operation.field);
  const fieldLabel = operationFieldLabel(field);

  if (op === 'clear_all') {
    return {
      name: 'clear_filters',
      args: { label: 'All filters' },
    };
  }

  if (op === 'clear_field') {
    return {
      name: 'remove_filter',
      args: {
        field,
        op: 'clear',
        value: null,
        label: `Cleared ${fieldLabel}`,
      },
    };
  }

  if (!field) {
    return { name: op || 'assistant_operation' };
  }

  const values = Array.isArray(operation.values) ? operation.values : [];
  const range = operation.range || null;

  let value = values;
  let label = `${op}: ${fieldLabel}`;

  if (range && typeof range === 'object') {
    value = range;
    label = `${fieldLabel}: ${range.gte ?? ''}${range.gte !== undefined || range.lte !== undefined ? '...' : ''}${range.lte ?? ''}`;
  } else if (values.length === 1) {
    value = values[0];
    label = `${fieldLabel}: ${formatOperationValue(field, values[0])}`;
  } else if (values.length > 1) {
    value = values;
    label = `${fieldLabel}: ${values.map((item) => formatOperationValue(field, item)).join(', ')}`;
  }

  return {
    name: 'apply_filter',
    args: {
      field,
      op,
      value,
      label,
    },
  };
}

function countryToCode(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return null;
  const direct = {
    denmark: 'DK',
    dk: 'DK',
    sweden: 'SE',
    se: 'SE',
    norway: 'NO',
    no: 'NO',
    germany: 'DE',
    de: 'DE',
    spain: 'ES',
    es: 'ES',
    'united kingdom': 'GB',
    uk: 'GB',
    britain: 'GB',
    england: 'GB',
    'united states': 'US',
    usa: 'US',
    us: 'US',
    'united states of america': 'US',
    netherlands: 'NL',
    nl: 'NL',
  };
  return direct[normalized] || normalized.toUpperCase();
}

function normalizeSeniority(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return null;
  const map = {
    'c-level': 'c_suite',
    'c level': 'c_suite',
    'c-suite': 'c_suite',
    chief: 'c_suite',
    vp: 'vp',
    director: 'director',
    head: 'head',
    manager: 'manager',
    senior: 'senior_ic',
    ic: 'ic',
  };
  return map[normalized] || normalized.replace(/\s+/g, '_');
}

function normalizeFunction(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return null;
  const map = {
    marketing: 'marketing',
    sales: 'sales',
    product: 'product',
    engineer: 'engineering',
    engineering: 'engineering',
    design: 'design',
    operation: 'operations',
    operations: 'operations',
    content: 'content',
  };
  return map[normalized] || null;
}

function toValueList(value) {
  if (Array.isArray(value)) return value.filter((item) => item !== undefined && item !== null);
  if (value === undefined || value === null || value === '') return [];
  return [value];
}

function addListValues(target, key, values) {
  const nextValues = dedupe(values.map((value) => normalizeString(value)).filter(Boolean));
  if (nextValues.length === 0) return;
  target[key] = dedupe([...(target[key] || []), ...nextValues]);
}

function ensureCompanyFilter(filters) {
  if (!filters.company || typeof filters.company !== 'object') {
    filters.company = {};
  }
  return filters.company;
}

function unionRanges(existing, incoming) {
  if (!incoming) return existing || undefined;
  const base = existing || {};
  const next = { ...base };

  if (incoming.gte !== undefined) {
    next.gte = next.gte === undefined ? incoming.gte : Math.min(next.gte, incoming.gte);
  }
  if (incoming.lte !== undefined) {
    next.lte = next.lte === undefined ? incoming.lte : Math.max(next.lte, incoming.lte);
  }

  return next;
}

function rangesToNumberRange(rangeValues) {
  const bounds = rangeValues
    .map((value) => EMPLOYEE_RANGE_BOUNDS[normalizeString(value)])
    .filter(Boolean);
  if (bounds.length === 0) return null;

  const gteValues = bounds.map((bound) => bound.gte).filter((value) => value !== undefined);
  const lteValues = bounds.map((bound) => bound.lte).filter((value) => value !== undefined);

  const range = {};
  if (gteValues.length > 0) range.gte = Math.min(...gteValues);
  if (lteValues.length > 0) range.lte = Math.max(...lteValues);
  return range;
}

function rangeFromFilter(filter = {}) {
  const op = normalizeString(filter.op);
  const value = filter.value;

  if (op === 'between' && value && typeof value === 'object') {
    return normalizeNumberRange(value);
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  if (op === 'gte') return { gte: numericValue };
  if (op === 'lte') return { lte: numericValue };
  if (op === 'equals') return { gte: numericValue, lte: numericValue };
  return null;
}

export function resolveAssistantFiltersFromResponse({ response, searchType }) {
  if (!response || typeof response !== 'object') {
    return { nextFilters: null, toolCalls: [] };
  }

  const operations = Array.isArray(response.operations_applied)
    ? response.operations_applied
    : [];
  const canonicalNextFilters = buildCanonicalFiltersFromAssistantResponse({
    nextFiltersPayload: response.next_filters,
    operations,
  });
  const nextFilters = canonicalNextFilters
    ? mapCanonicalFiltersToUi(searchType, canonicalNextFilters)
    : null;

  const toolCalls = operations.map((operation) => operationToToolCall(operation));

  return {
    nextFilters,
    toolCalls,
  };
}

function buildCanonicalFiltersFromAssistantResponse({
  nextFiltersPayload,
  operations,
}) {
  const hasNextFilters = nextFiltersPayload && typeof nextFiltersPayload === 'object';
  if (!hasNextFilters && (!Array.isArray(operations) || operations.length === 0)) {
    return null;
  }

  if (!hasNextFilters) {
    const derived = {};
    for (const operation of operations || []) {
      applyCanonicalOperation(derived, operation);
    }
    return derived;
  }

  const canonical = JSON.parse(JSON.stringify(nextFiltersPayload));
  if (!Array.isArray(operations) || operations.length === 0) {
    return canonical;
  }

  const derived = {};
  for (const operation of operations) {
    applyCanonicalOperation(derived, operation);
  }

  for (const path of collectPaths(derived)) {
    if (getPath(canonical, path) !== undefined) continue;
    const value = getPath(derived, path);
    if (value === undefined) continue;
    setPath(canonical, path, value);
  }

  return canonical;
}

function applyCanonicalOperation(canonical, operation = {}) {
  const op = normalizeString(operation.op || operation.type || operation.name);
  const fieldKey = normalizeString(operation.field_key || operation.field);

  if (op === 'clear_all') {
    Object.keys(canonical).forEach((key) => {
      delete canonical[key];
    });
    return;
  }

  if (!fieldKey) return;

  if (op === 'clear_field') {
    deletePath(canonical, fieldKey);
    return;
  }

  if (op === 'set_range') {
    const range = normalizeOperationRange(operation.range);
    if (!range) return;
    setPath(canonical, fieldKey, range);
    return;
  }

  const values = toValueList(operation.values).map((value) => normalizeString(value)).filter(Boolean);
  if (values.length === 0) return;

  const normalizedValues = isIndustryFieldKey(fieldKey)
    ? canonicalizeIndustryValues(values)
    : values;
  if (normalizedValues.length === 0) return;

  const currentValue = getPath(canonical, fieldKey);
  const existingValues = toValueList(currentValue).map((value) => normalizeString(value)).filter(Boolean);

  if (op === 'set_values') {
    setPath(canonical, fieldKey, dedupe(normalizedValues));
    return;
  }

  if (op === 'add_values') {
    const normalizedExisting = isIndustryFieldKey(fieldKey)
      ? canonicalizeIndustryValues(existingValues)
      : existingValues;
    setPath(canonical, fieldKey, dedupe([...normalizedExisting, ...normalizedValues]));
    return;
  }

  if (op === 'remove_values') {
    const normalizedExisting = isIndustryFieldKey(fieldKey)
      ? canonicalizeIndustryValues(existingValues)
      : existingValues;
    const removals = new Set(normalizedValues);
    const nextValues = normalizedExisting.filter((value) => !removals.has(value));
    if (nextValues.length > 0) {
      setPath(canonical, fieldKey, nextValues);
    } else {
      deletePath(canonical, fieldKey);
    }
  }
}

function normalizeOperationRange(range) {
  if (!range || typeof range !== 'object') return null;
  const gte = range.gte !== undefined && range.gte !== null ? Number(range.gte) : null;
  const lte = range.lte !== undefined && range.lte !== null ? Number(range.lte) : null;

  const next = {};
  if (Number.isFinite(gte)) next.gte = gte;
  if (Number.isFinite(lte)) next.lte = lte;
  return Object.keys(next).length > 0 ? next : null;
}

function getPath(data, fieldKey) {
  const parts = fieldKey.split('.').filter(Boolean);
  if (parts.length === 0) return undefined;

  let current = data;
  for (const part of parts) {
    if (!current || typeof current !== 'object' || !(part in current)) return undefined;
    current = current[part];
  }
  return current;
}

function setPath(data, fieldKey, value) {
  const parts = fieldKey.split('.').filter(Boolean);
  if (parts.length === 0) return;

  let current = data;
  for (const part of parts.slice(0, -1)) {
    if (!current[part] || typeof current[part] !== 'object' || Array.isArray(current[part])) {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
}

function deletePath(data, fieldKey) {
  const parts = fieldKey.split('.').filter(Boolean);
  if (parts.length === 0) return;

  let current = data;
  for (const part of parts.slice(0, -1)) {
    if (!current || typeof current !== 'object' || !(part in current)) return;
    current = current[part];
  }
  if (!current || typeof current !== 'object') return;
  delete current[parts[parts.length - 1]];
}

function collectPaths(value, prefix = '') {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return prefix ? [prefix] : [];
  }

  const paths = [];
  for (const [key, nested] of Object.entries(value)) {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      paths.push(...collectPaths(nested, nextPrefix));
      continue;
    }
    paths.push(nextPrefix);
  }
  return paths;
}

function operationFieldLabel(fieldKey) {
  const labels = {
    country_codes: 'Country',
    cities: 'City',
    industries: 'Industry',
    employee_count_ranges: 'Employee Range',
    founded_year: 'Founded year',
    seniorities: 'Seniority',
    functions: 'Role',
    titles_any: 'Role title',
    highlights_any: 'Signal',
    company_tags_any: 'Signal',
    'company.employee_count': 'Company size',
  };
  return labels[fieldKey] || fieldKey;
}

function formatOperationValue(fieldKey, value) {
  if (fieldKey === 'country_codes') {
    return countryCodeToLabel(value);
  }
  return value;
}
